import {
  Text,
  GreyGrey10,
  GreyGrey30,
  GreyGrey70,
  GreyGrey80,
  IconFileBend,
  Image,
  Primary13Regular,
  ShadowXs,
  Box,
  Flex
} from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import { PdfThumbnail } from '@sequencehq/pdf-viewer'
import { useSetAtom } from 'jotai'
import { showAttachmentInInspectorAtom } from './attachments.atom'
import { ServerAsset } from '@sequencehq/api'

type Props = {
  attachments: ServerAsset[]
  testIdPrefix: string
}

export const AttachmentsCarousel = ({ attachments, testIdPrefix }: Props) => {
  const setShowAttachmentInInspector = useSetAtom(showAttachmentInInspectorAtom)

  return (
    <Flex gap="24px" width="100%" overflowX="auto">
      {attachments.map(attachment => {
        return (
          <Flex
            data-testid={`${testIdPrefix}${attachment.id}`}
            flexDirection="column"
            gap="8px"
            key={attachment.id}
            cursor={attachment.type === 'DOCUMENT' ? 'pointer' : 'default'}
            onClick={() => {
              if (attachment.type === 'DOCUMENT') {
                setShowAttachmentInInspector(attachment.id)
              }
            }}
          >
            <Box
              borderRadius="6px"
              overflow="hidden"
              height="180px"
              width="144px"
              border={`1px solid ${GreyGrey30}`}
              boxShadow={ShadowXs}
              backgroundColor={GreyGrey10}
            >
              <Box
                height="100%"
                width="100%"
                transition="transform 0.25s"
                position="relative"
                _after={{
                  content: '""',
                  transition: 'opacity 0.25s',
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  backgroundColor: 'rgba(0,0,0,0.08)',
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  zIndex: 2
                }}
                _hover={{
                  transform: 'scale(1.05)',
                  _after: {
                    opacity: 1
                  }
                }}
                zIndex="1"
              >
                {match(attachment.type)
                  .with('DOCUMENT', () => <PdfThumbnail src={attachment.url} />)
                  .with('IMAGE', () => (
                    <Flex
                      alignContent="center"
                      justifyContent="center"
                      height="100%"
                      width="100%"
                    >
                      <Image
                        maxWidth="100%"
                        objectFit="contain"
                        src={attachment.url}
                      />
                    </Flex>
                  ))
                  .with('BINARY', () => (
                    // height matches the thumbnail tile so the icon centres
                    // vertically; the wrapper's height="100%" does not resolve here
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      height="180px"
                      width="100%"
                    >
                      <IconFileBend size={48} color={GreyGrey70} />
                    </Flex>
                  ))
                  .exhaustive()}
              </Box>
            </Box>
            <Text
              width="144px"
              noOfLines={2}
              _groupHover={{
                noOfLines: 1
              }}
              overflow="hidden"
              textOverflow="ellipsis"
              wordBreak="break-all"
              {...Primary13Regular}
              color={GreyGrey80}
            >
              {attachment.fileName}
            </Text>
          </Flex>
        )
      })}
    </Flex>
  )
}
